import { MonitorSettingsBase, MonitorSettings, MonitorSettingsEntry, MonitorSettingsEntryKey } from './MonitorSettings';
import { ValueEncoder } from "../Utils/ValueEncoder";
import { MonitorType } from "../Core/MonitorType";
import { Dictionary, Guid, HashSet, List, ObjUtils, StringUtils } from '../Utils';
import { MonitorPolicy } from '.';


export class MonitorSettings_Snmp extends MonitorSettings<SnmpMonitorEntry> {

	constructor() {
		super();

		this.MonitorType = MonitorType.SNMP;
	}

	protected get ObjSerEntriesName(): string {
		return "snmp_monitor_table";
	}

	protected CreateEntry(): SnmpMonitorEntry {
		return new SnmpMonitorEntry();
	}

	public Find_Oid(snmpOid: string, policyGuid: Guid, deviceGuid: Guid): SnmpMonitorEntry {
		let snmpEntry: SnmpMonitorEntry = null;

		if (snmpOid) {
			this.GetEntries().Some((entry: SnmpMonitorEntry) => {
				if (entry.Oid != snmpOid) {
					return false;
				}
				if (policyGuid) {
					if (!entry.PolicyGuid.Equals(policyGuid)) {
						return false;
					}
				}
				if (deviceGuid) {
					if (!entry.DeviceGuid.Equals(deviceGuid)) {
						return false;
					}
				}

				snmpEntry = entry;
				return true;
			});
		}

		return snmpEntry;
	}


	public Find(otherEntry: SnmpMonitorEntry): SnmpMonitorEntry {
		if (!otherEntry)
			return null;

		let entry: SnmpMonitorEntry = null;

		if (otherEntry.HasOid) {
			entry = this.Find_Guids(otherEntry.Guid, otherEntry.PolicyGuid, otherEntry.DeviceGuid);
			if (!entry && otherEntry.HasOid) {
				entry = this.Find_Oid(otherEntry.Oid, otherEntry.PolicyGuid, otherEntry.DeviceGuid);
			}
		}

		return entry;
	}

	public VerifyGUIDs(policySettings: MonitorSettingsBase): boolean {
		let guidAdded: boolean = false;

		if (policySettings) {
			let parentPolicySettings: MonitorSettings_Snmp = policySettings as MonitorSettings_Snmp;
			if (parentPolicySettings) {
				this.GetEntries().ForEach((deviceSnmpEntry: SnmpMonitorEntry) => {
					let parentEntry: SnmpMonitorEntry = parentPolicySettings.Find(deviceSnmpEntry);
					if (parentEntry) {
						console.assert(parentEntry.HasGuid);

						if (deviceSnmpEntry.Guid != parentEntry.Guid) {
							deviceSnmpEntry.Guid = parentEntry.Guid;
							guidAdded = true;
						}
					}
				});
			}
		}

		return guidAdded;
	}

	public CopySettings(other: MonitorSettingsBase): void {
		if (!other)
			return;

		if (other == this)
			return;

		let otherSettings = other as MonitorSettings_Snmp;
		if (!otherSettings)
			return;

		super.CopySettings(other);

		let policyGuid = Guid.Empty;
		if (otherSettings.HasMonitorPolicyId)
			policyGuid = otherSettings.MonitorPolicyId;

		// CYCLE thru the Policy.Settings to ADD/UPDATE the config on the target settings
		otherSettings.GetEntries().ForEach((otherEntry: SnmpMonitorEntry) => {
			if (!otherEntry.HasPolicyGuid && !policyGuid.Equals(Guid.Empty))
				otherEntry.PolicyGuid = policyGuid;

			let snmpEntry: SnmpMonitorEntry = null;

			if (otherEntry.HasGuid) {
				snmpEntry = this.Find_Guids(otherEntry.Guid, otherEntry.PolicyGuid, otherEntry.DeviceGuid);
			}

			if (!snmpEntry && otherEntry.HasOid) {
				snmpEntry = this.Find_Oid(otherEntry.Oid, otherEntry.PolicyGuid, otherEntry.DeviceGuid);
			}

			if (!snmpEntry) {
				snmpEntry = this.AddCopyConfig(otherEntry);
			}
			else {
				snmpEntry.CopyConfig(otherEntry);
			}
		});

		// REMOVE any config that has been deleted.
		let removeKeys = new HashSet<MonitorSettingsEntryKey>();

		this.GetEntries().ForEach((snmpEntry: SnmpMonitorEntry) => {
			if (!snmpEntry.HasPolicyGuid && this.HasMonitorPolicyId)
				snmpEntry.PolicyGuid = this.MonitorPolicyId;

			let otherEntry: SnmpMonitorEntry = otherSettings.Find(snmpEntry);
			if (!otherEntry) {
				let key = new MonitorSettingsEntryKey(snmpEntry);
				removeKeys.Add(key);
			}
		});

		removeKeys.ForEach((key: MonitorSettingsEntryKey) => {
			this.Remove_Guids(key.Guid, key.PolicyGuid, key.DeviceGuid);
		});
	}

	public GetGenericEntries(): Dictionary<string, SnmpMonitorEntry> {
		let genericEntries = new Dictionary<string, SnmpMonitorEntry>();

		if (this.Count > 0) {
			this.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
				if (!entry.HasDeviceGuid) {
					let oidStr: string = entry.Oid;
					console.assert(!genericEntries.ContainsKey(oidStr));
					if (!genericEntries.ContainsKey(oidStr)) {
						genericEntries.Add(oidStr, entry);
					}
				}
			});
		}

		return genericEntries;
	}

	public GetDeviceEntries(): Dictionary<string, SnmpMonitorEntry> {
		let deviceEntries = new Dictionary<string, SnmpMonitorEntry>();

		if (this.Count > 0) {
			this.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
				if (entry.HasDeviceGuid) {
					deviceEntries.Add(entry.Oid, entry);
				}
			});
		}

		return deviceEntries;
	}

	public GetEntriesForDevice(deviceGuid: Guid): Dictionary<string, SnmpMonitorEntry> {
		let deviceEntries = new Dictionary<string, SnmpMonitorEntry>();

		if (this.Count > 0) {
			this.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
				if (entry.HasDeviceGuid && entry.DeviceGuid.Equals(deviceGuid)) {
					deviceEntries.Add(entry.Oid, entry);
				}
			});
		}

		return deviceEntries;
	}

	public RemoveDevice(deviceGUID: Guid): void {
		if (deviceGUID.Equals(Guid.Empty))
			return;

		let removeList = new List<SnmpMonitorEntry>();
		this.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
			if (entry.HasDeviceGuid && entry.DeviceGuid.Equals(deviceGUID)) {
				removeList.Add(entry);
			}
		});

		if (removeList.Count > 0) {
			removeList.ForEach((entry: SnmpMonitorEntry) => {
				this.Remove(entry);
			});
		}
	}

	public RemoveSnmpOid(snmpOid: string): void {
		if (!snmpOid)
			return;
		if (StringUtils.IsNullOrWhiteSpace(snmpOid))
			return;

		let removeList = new List<SnmpMonitorEntry>();
		this.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
			if (entry.HasOid && (entry.Oid === snmpOid)) {
				removeList.Add(entry);
			}
		});

		if (removeList.Count > 0) {
			removeList.ForEach((entry: SnmpMonitorEntry) => {
				this.Remove(entry);
			});
		}
	}

	private IsGuidEmpty(guid: Guid): boolean {
		return (!guid || guid.Equals(Guid.Empty));
	}

	public AddUpdateSnmpMonitorForDevice(policyGUID: Guid, deviceGUID: Guid, snmpMonitorEntry: SnmpMonitorEntry): boolean {
		if (this.IsGuidEmpty(policyGUID))
			return false;
		if (this.IsGuidEmpty(deviceGUID))
			return false;
		if (!snmpMonitorEntry || !snmpMonitorEntry.HasOid)
			return false;

		console.assert(snmpMonitorEntry.HasGuid);
		console.assert(policyGUID.Equals(snmpMonitorEntry.PolicyGuid));

		let addOK: boolean = false;

		let oldEntry: SnmpMonitorEntry = this.Find_Oid(snmpMonitorEntry.Oid, policyGUID, deviceGUID);
		if (!oldEntry) {
			let newEntry = new SnmpMonitorEntry(snmpMonitorEntry);
			newEntry.Guid = snmpMonitorEntry.Guid;
			newEntry.PolicyGuid = policyGUID;
			newEntry.DeviceGuid = deviceGUID;

			oldEntry = this.Add(newEntry);
		}
		else {
			let cachedDeviceGUID: Guid = snmpMonitorEntry.DeviceGuid;

			snmpMonitorEntry.DeviceGuid = deviceGUID;
			oldEntry.CopyConfig(snmpMonitorEntry);

			snmpMonitorEntry.DeviceGuid = cachedDeviceGUID;
		}

		if (oldEntry)
			addOK = true;

		return addOK;
	}

	public AddUpdateSnmpMonitorForPolicy(monitorPolicy: MonitorPolicy, snmpMonitorEntry: SnmpMonitorEntry): number {
		console.assert(!!monitorPolicy);
		console.assert(this.MonitorType == MonitorType.SNMP);
		console.assert(snmpMonitorEntry.PolicyGuid.Equals(monitorPolicy.MonitorPolicyId));

		if (!monitorPolicy)
			return 0;
		if (!snmpMonitorEntry || !snmpMonitorEntry.HasOid)
			return 0;

		if (!snmpMonitorEntry.HasGuid) {
			snmpMonitorEntry.GenerateGuid();
		}

		let addCount: number = 0;

		let deviceGuids = new HashSet<Guid>();
		deviceGuids.Add(Guid.Empty);	// Account for generic entry
		deviceGuids.UnionWith(monitorPolicy.DeviceGuids);
		deviceGuids.ForEach((deviceGUID: Guid) => {
			let oldEntry: SnmpMonitorEntry = this.Find_Oid(snmpMonitorEntry.Oid, monitorPolicy.MonitorPolicyId, deviceGUID);
			if (!oldEntry) {
				let newEntry = new SnmpMonitorEntry(snmpMonitorEntry);
				newEntry.Guid = snmpMonitorEntry.Guid;
				newEntry.PolicyGuid = monitorPolicy.MonitorPolicyId;
				newEntry.DeviceGuid = deviceGUID;

				oldEntry = this.Add(newEntry);
			}
			else {
				console.assert(oldEntry.HasGuid && oldEntry.Guid.Equals(snmpMonitorEntry.Guid));
				console.assert(oldEntry.HasPolicyGuid && oldEntry.PolicyGuid.Equals(snmpMonitorEntry.PolicyGuid));
				console.assert(oldEntry.HasDeviceGuid && oldEntry.DeviceGuid.Equals(snmpMonitorEntry.DeviceGuid));

				oldEntry.CopyConfig(snmpMonitorEntry);
			}

			if (oldEntry != null) {
				addCount++;
			}
		});

		return addCount;
	}

	public Compare(other: MonitorSettings_Snmp, output: string[]): void {
		ObjUtils.Compare(this.MonitorType, other.MonitorType, "MonitorSettings_Snmp MonitorType", output);
		ObjUtils.Compare(this.MonitorPolicyId, other.MonitorPolicyId, "MonitorSettings_Snmp MonitorPolicyId", output);
		ObjUtils.Compare(this.MonitorProtocol, other.MonitorProtocol, "MonitorSettings_Snmp MonitorProtocol", output);
		ObjUtils.Compare(this.PollByHostname, other.PollByHostname, "MonitorSettings_Snmp PollByHostname", output);
		ObjUtils.Compare(this.UseThresholds, other.UseThresholds, "MonitorSettings_Snmp UseThresholds", output);
		ObjUtils.Compare(this.CriticalThreshold, other.CriticalThreshold, "MonitorSettings_Snmp CriticalThreshold", output);
		ObjUtils.Compare(this.WarningThreshold, other.WarningThreshold, "MonitorSettings_Snmp WarningThreshold", output);
		ObjUtils.Compare(this.LimitCount, other.LimitCount, "MonitorSettings_Snmp LimitCount", output);
		ObjUtils.Compare(this.GetEntries().Count, other.GetEntries().Count, "MonitorSettings_Snmp GetEntries().Count", output);

		if (this.GetEntries().Count == other.GetEntries().Count) {
			for (let i = 0; i < this.GetEntries().Count; ++i) {
				let thisEntry = <SnmpMonitorEntry>this.GetEntries().Get(i);
				let otherEntry = <SnmpMonitorEntry>this.GetEntries().Get(i);
				thisEntry.Compare(otherEntry, output);
			}
		}
	}

	public Write(obj: any, context: any): void {
		obj.monitor_settings = {};
		obj = obj.monitor_settings;

		super.Write(obj, context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitor_settings)
			throw new Error();
		obj = obj.monitor_settings;

		super.Read(obj, context);
	}

}


export class SnmpMonitorEntry extends MonitorSettingsEntry {

	public Oid: string;
	public get HasOid(): boolean {
		return !!this.Oid;
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public Category: string;
	public get HasCategory(): boolean {
		return !!this.Category;
	}

	public ValueType: string;
	public get HasValueType(): boolean {
		return !!this.ValueType;
	}

	public Value: string;
	public get HasValue(): boolean {
		return !!this.Value;
	}

	public CollectOnly: boolean;
	public UseExpectedValue: boolean;

	public ExpectedValue: string;
	public get HasExpectedValue(): boolean {
		return !!this.ExpectedValue;
	}

	public ExpectedValue_UseNotEqual: boolean;
	public UseThresholds: boolean;
	public CriticalThreshold: number;
	public WarningThreshold: number;
	public Thresholds_UseLessThan: boolean;


	constructor(oidOrEntry?: string | SnmpMonitorEntry) {
		super();

		this._Init();

		if (oidOrEntry) {
			if (oidOrEntry instanceof SnmpMonitorEntry) {
				this.CopyConfig(oidOrEntry as SnmpMonitorEntry);
			}
			else {
				this.Oid = oidOrEntry as string;
			}
		}
	}

	private _Init(): void {
		this.Oid = "";
		this.Name = "";
		this.Category = "";
		this.ValueType = "";
		this.Value = "";

		this.CollectOnly = false;

		this.UseExpectedValue = true;
		this.ExpectedValue = "";
		this.ExpectedValue_UseNotEqual = false;

		this.UseThresholds = false;
		this.CriticalThreshold = 0;
		this.WarningThreshold = 0;
		this.Thresholds_UseLessThan = false;
	}

	public CopyConfig(other: SnmpMonitorEntry): void {
		if (!other)
			return;
		if (other == this)
			return;

		super.CopyConfig(other);

		this.Oid = other.Oid;
		this.Name = other.Name;
		this.Category = other.Category;
		this.ValueType = other.ValueType;
		this.Value = other.Value;

		this.CollectOnly = other.CollectOnly;

		this.UseExpectedValue = other.UseExpectedValue;
		this.ExpectedValue = other.ExpectedValue;
		this.ExpectedValue_UseNotEqual = other.ExpectedValue_UseNotEqual;

		this.UseThresholds = other.UseThresholds;
		this.CriticalThreshold = other.CriticalThreshold;
		this.WarningThreshold = other.WarningThreshold;
		this.Thresholds_UseLessThan = other.Thresholds_UseLessThan;
	}

	public get DisplayName(): string {
		let displayName = "";

		if (this.HasName)
			displayName = `${this.Name} - ${this.Oid}`;
		else
			displayName = `${this.Oid}`;

		return displayName;
	}

	public Compare(other: SnmpMonitorEntry, output: string[]): void {
		ObjUtils.Compare(this.Guid, other.Guid, "SnmpMonitorEntry Guid", output);
		ObjUtils.Compare(this.PolicyGuid, other.PolicyGuid, "SnmpMonitorEntry PolicyGuid", output);
		ObjUtils.Compare(this.DeviceGuid, other.DeviceGuid, "SnmpMonitorEntry DeviceGuid", output);
		ObjUtils.Compare(this.Oid, other.Oid, "SnmpMonitorEntry Oid", output);
		ObjUtils.Compare(this.Name, other.Name, "SnmpMonitorEntry Name", output);
		ObjUtils.Compare(this.Category, other.Category, "SnmpMonitorEntry Category", output);
		ObjUtils.Compare(this.ValueType, other.ValueType, "SnmpMonitorEntry ValueType", output);
		ObjUtils.Compare(this.Value, other.Value, "SnmpMonitorEntry Value", output);
		ObjUtils.Compare(this.CollectOnly, other.CollectOnly, "SnmpMonitorEntry CollectOnly", output);
		ObjUtils.Compare(this.ExpectedValue, other.ExpectedValue, "SnmpMonitorEntry ExpectedValue", output);
		ObjUtils.Compare(this.ExpectedValue_UseNotEqual, other.ExpectedValue_UseNotEqual, "SnmpMonitorEntry ExpectedValue_UseNotEqual", output);
		ObjUtils.Compare(this.UseThresholds, other.UseThresholds, "SnmpMonitorEntry UseThresholds", output);
		ObjUtils.Compare(this.CriticalThreshold, other.CriticalThreshold, "SnmpMonitorEntry CriticalThreshold", output);
		ObjUtils.Compare(this.WarningThreshold, other.WarningThreshold, "SnmpMonitorEntry WarningThreshold", output);
		ObjUtils.Compare(this.Thresholds_UseLessThan, other.Thresholds_UseLessThan, "SnmpMonitorEntry Thresholds_UseLessThan", output);
	}

	public Write(obj: any, context: any): void {
		obj.snmp_monitor_entry = {};
		obj = obj.snmp_monitor_entry;

		super.Write(obj, context);

		obj.oid = this.Oid;

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasCategory)
			obj.category = this.Category;

		if (this.HasValueType)
			obj.value_type = this.ValueType;

		if (this.HasValue)
			obj.value = this.Value;

		obj.collect_only = ValueEncoder.EncodeBoolean(this.CollectOnly);
		obj.use_expected_value = ValueEncoder.EncodeBoolean(this.UseExpectedValue);

		if (this.HasExpectedValue)
			obj.expected_value = this.ExpectedValue;

		obj.expected_value_use_not_equal = ValueEncoder.EncodeBoolean(this.ExpectedValue_UseNotEqual);
		obj.use_threshold_values = ValueEncoder.EncodeBoolean(this.UseThresholds);
		obj.critical_threshold = ValueEncoder.EncodeDouble(this.CriticalThreshold);
		obj.warning_threshold = ValueEncoder.EncodeDouble(this.WarningThreshold);
		obj.threshold_values_use_less_than = ValueEncoder.EncodeBoolean(this.Thresholds_UseLessThan);
	}

	public Read(obj: any, context: any): void {
		if (!obj.snmp_monitor_entry)
			throw new Error();
		obj = obj.snmp_monitor_entry;

		super.Read(obj, context);

		if (obj.oid !== undefined)
			this.Oid = obj.oid;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.category !== undefined)
			this.Category = obj.category;

		if (obj.value_type !== undefined)
			this.ValueType = obj.value_type;

		if (obj.value !== undefined)
			this.Value = obj.value;

		if (obj.collect_only !== undefined)
			this.CollectOnly = ValueEncoder.DecodeBoolean(obj.collect_only);

		if (obj.use_expected_value !== undefined)
			this.UseExpectedValue = ValueEncoder.DecodeBoolean(obj.use_expected_value);

		if (obj.expected_value !== undefined)
			this.ExpectedValue = obj.expected_value;

		if (obj.expected_value_use_not_equal !== undefined)
			this.ExpectedValue_UseNotEqual = ValueEncoder.DecodeBoolean(obj.expected_value_use_not_equal);

		if (obj.use_threshold_values !== undefined)
			this.UseThresholds = ValueEncoder.DecodeBoolean(obj.use_threshold_values);

		if (obj.critical_threshold !== undefined)
			this.CriticalThreshold = ValueEncoder.DecodeDouble(obj.critical_threshold);

		if (obj.warning_threshold !== undefined)
			this.WarningThreshold = ValueEncoder.DecodeDouble(obj.warning_threshold);

		if (obj.threshold_values_use_less_than !== undefined)
			this.Thresholds_UseLessThan = ValueEncoder.DecodeBoolean(obj.threshold_values_use_less_than);
	}

}
